import type { InvoiceMailLogEntry, InvoiceMailState } from '@embertime/shared'
import {
  AlertTriangle,
  Archive,
  Check,
  ChevronRight,
  Clock3,
  Download,
  FileText,
  Loader2,
  Mail,
  RefreshCw,
} from 'lucide-react'
import type { InvoiceDetail } from '../../lib/api'
import { api } from '../../lib/api'
import { cn } from '../../lib/utils'
import {
  archiveText,
  fileSize,
  formatLogTime,
  pdfFormatLabel,
  previousDispatch,
  statusText,
} from './invoice-mail-logic'

// #536 Rechnung per Mail in the invoice view. Mockups 536_mockup_einstieg,
// _ergebnis and _gesperrt: invoice data (PDF + recipient), the dispatch card,
// the durable dispatch log and the evidence (Message-ID). Green journal text
// only for a stored `verified` proof; SMTP acceptance is never "delivered".

const sectionTitle = 'mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-text-tertiary'
const card = 'overflow-hidden rounded-2xl border border-border-default bg-bg-elevated/50'

function ResultBanner({ entry }: { entry: InvoiceMailLogEntry }) {
  if (entry.status === 'sent')
    return (
      <div className="rounded-2xl border border-success/40 bg-success/10 p-5" role="status">
        <div className="flex items-start gap-3">
          <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-success" />
          <div>
            <h2 className="text-base font-semibold">E-Mail versendet</h2>
            <p className="mt-1 text-sm leading-relaxed text-text-secondary">
              Der Mailserver hat die Rechnung an {entry.recipient} zum Versand angenommen.{' '}
              {entry.archiveStatus === 'verified'
                ? 'Eine Kopie liegt im Journal-Archiv.'
                : 'Archivnachweis ausstehend.'}
            </p>
          </div>
        </div>
      </div>
    )
  if (entry.status === 'failed') return null
  return (
    <div className="rounded-2xl border border-warning/40 bg-warning/10 p-5" role="status">
      <div className="flex items-start gap-3">
        <AlertTriangle aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
        <div>
          <h2 className="text-base font-semibold">Versandausgang unklar</h2>
          <p className="mt-1 text-sm leading-relaxed text-text-secondary">
            Ob der Mailserver die Rechnung angenommen hat, ist nicht sicher. Embertime sendet nicht
            automatisch erneut. Bitte beim Empfänger nachfragen, bevor Sie bewusst erneut senden.
          </p>
        </div>
      </div>
    </div>
  )
}

function DispatchLog({
  entries,
  onArchiveCheck,
  checking,
}: {
  entries: InvoiceMailLogEntry[]
  onArchiveCheck?: (attemptId: string) => void
  checking?: string | null
}) {
  return (
    <div>
      <h3 className={sectionTitle}>Versandprotokoll</h3>
      <div className={card}>
        {entries.map((entry) => (
          <div key={entry.attemptId} className="border-b border-border-subtle last:border-b-0">
            <div className="flex items-start gap-3 px-4 py-3">
              {entry.status === 'sent' ? (
                <Check aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-success" />
              ) : (
                <AlertTriangle
                  aria-hidden="true"
                  className="mt-0.5 h-5 w-5 shrink-0 text-warning"
                />
              )}
              <div className="min-w-0">
                <strong className="block text-sm">
                  {statusText(entry)} · {formatLogTime(entry.acceptedAt ?? entry.reservedAt)}
                </strong>
                <p className="mt-0.5 truncate text-xs text-text-tertiary">
                  Von {entry.fromAddress} · {entry.companyName}
                </p>
                <p className="mt-0.5 truncate text-xs text-text-tertiary">An {entry.recipient}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 border-t border-border-subtle/60 px-4 py-3">
              <FileText aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-text-tertiary" />
              <div className="min-w-0">
                <strong className="block text-sm">
                  {entry.status === 'sent' ? 'PDF angehängt' : 'PDF vorbereitet'}
                </strong>
                <p className="mt-0.5 truncate text-xs text-text-tertiary">
                  {entry.pdfName} · {fileSize(entry.pdfSize)} · {pdfFormatLabel(entry.pdfFormat)}
                </p>
              </div>
            </div>
            {entry.status === 'sent' && (
              <div className="flex items-center gap-3 border-t border-border-subtle/60 px-4 py-3">
                <Archive
                  aria-hidden="true"
                  className={cn(
                    'h-5 w-5 shrink-0',
                    entry.archiveStatus === 'verified' ? 'text-success' : 'text-text-tertiary',
                  )}
                />
                <div className="min-w-0 flex-1">
                  <strong className="block text-sm">{archiveText(entry)}</strong>
                  <p className="mt-0.5 text-xs text-text-tertiary">
                    {entry.archiveStatus === 'verified'
                      ? 'Nachweis an dieser Rechnung gespeichert'
                      : 'Kopie im Journal-Archiv noch nicht bestätigt'}
                  </p>
                </div>
                {entry.archiveStatus !== 'verified' && onArchiveCheck && (
                  <button
                    type="button"
                    onClick={() => onArchiveCheck(entry.attemptId)}
                    disabled={checking === entry.attemptId}
                    className="flex min-h-11 shrink-0 items-center gap-1.5 rounded-xl border border-border-default px-3 text-xs font-semibold hover:bg-bg-overlay disabled:opacity-60"
                  >
                    {checking === entry.attemptId ? (
                      <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
                    ) : (
                      <RefreshCw aria-hidden="true" className="h-4 w-4" />
                    )}
                    Prüfen
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export function InvoiceMailOverview({
  invoice,
  state,
  entries = [],
  onOpenSheet,
  onArchiveCheck,
  checking = null,
  archiveError = null,
}: {
  invoice: InvoiceDetail
  state: InvoiceMailState | undefined
  entries?: InvoiceMailLogEntry[]
  onOpenSheet?: () => void
  onArchiveCheck?: (attemptId: string) => void
  checking?: string | null
  archiveError?: string | null
}) {
  const name = state?.pdf.name ?? `${invoice.invoiceNumber}.pdf`
  const latest = entries[0]
  const previous = previousDispatch(entries)
  const latestWithId = entries.find((entry) => entry.messageId && entry.status === 'sent')
  const locked = !state?.enabled
  const usableSender = state?.senders.some((sender) => sender.blockedCode === null) ?? false

  if (locked)
    return (
      <section aria-label="Rechnung per E-Mail" className="space-y-5">
        {latest && <ResultBanner entry={latest} />}
        {entries.length > 0 && <DispatchLog entries={entries} />}
        <div className="rounded-2xl border border-warning/40 bg-warning/10 p-5 md:p-6">
          <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-warning/15 text-warning">
            <AlertTriangle aria-hidden="true" className="h-5 w-5" />
          </div>
          <h2 className="text-xl font-semibold leading-tight">Versand noch nicht freigegeben</h2>
          <p className="mt-2 text-sm leading-relaxed text-text-secondary">
            Der Mailserver befindet sich im Warm-up. Geschäftliche Rechnungen können erst nach der
            erfolgreichen Zustellprüfung per Mail versendet werden.
          </p>
        </div>

        <div>
          <h3 className={sectionTitle}>Rechnung bleibt bereit</h3>
          <div className={card}>
            <a
              href={api.invoicePdfUrl(invoice.id, { download: true })}
              className="flex min-h-16 items-center gap-3 px-4 py-3 hover:bg-bg-overlay/40"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
                <FileText aria-hidden="true" className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <strong className="block truncate text-sm">{name}</strong>
                <span className="block text-xs text-text-tertiary">
                  {state
                    ? `${fileSize(state.pdf.size)} · Download weiterhin verfügbar`
                    : 'Download weiterhin verfügbar'}
                </span>
              </span>
            </a>
            <div className="flex min-h-16 items-center gap-3 border-t border-border-subtle px-4 py-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bg-overlay text-text-secondary">
                <Clock3 aria-hidden="true" className="h-5 w-5" />
              </span>
              <span>
                <strong className="block text-sm">
                  {entries.length > 0 ? 'Neuer Versand gesperrt' : 'Kein Versand über Embertime'}
                </strong>
                <span className="block text-xs text-text-tertiary">
                  {entries.length > 0
                    ? 'Frühere Einträge bleiben sichtbar'
                    : 'Keine Mail und kein Versandprotokoll erzeugt'}
                </span>
              </span>
            </div>
          </div>
        </div>

        <div className="grid gap-2">
          <a
            href={api.invoicePdfUrl(invoice.id, { download: true })}
            className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border-default bg-bg-elevated text-sm font-semibold hover:bg-bg-overlay"
          >
            <Download aria-hidden="true" className="h-4 w-4" /> PDF herunterladen
          </a>
          <button
            type="button"
            disabled
            className="flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border-default bg-bg-overlay text-sm font-semibold text-text-tertiary opacity-60"
          >
            <Mail aria-hidden="true" className="h-4 w-4" /> Per Mail senden
          </button>
          <p className="text-center text-xs text-text-tertiary">
            Freigabe nach Abschluss der Zustellprüfung (#533)
          </p>
        </div>
      </section>
    )

  const dataCard = (
    <div>
      <h3 className={sectionTitle}>Rechnungsdaten</h3>
      <div className={card}>
        <a
          href={api.invoicePdfUrl(invoice.id, { download: true })}
          className="flex min-h-16 items-center gap-3 px-4 py-3 hover:bg-bg-overlay/40"
        >
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-bg-overlay text-text-secondary">
            <FileText aria-hidden="true" className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <strong className="block text-sm">Rechnungs-PDF</strong>
            <span className="block truncate text-xs text-text-tertiary">
              {name}
              {state ? ` · ${fileSize(state.pdf.size)}` : ''}
            </span>
          </span>
          <ChevronRight aria-hidden="true" className="h-5 w-5 shrink-0 text-text-tertiary" />
        </a>
        <div className="flex min-h-16 items-center gap-3 border-t border-border-subtle px-4 py-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-bg-overlay text-text-secondary">
            <Mail aria-hidden="true" className="h-5 w-5" />
          </span>
          <span className="min-w-0">
            <strong className="block text-sm">Empfänger</strong>
            <span className="block truncate text-xs text-text-tertiary">
              {state?.recipient ?? 'Keine Rechnungsadresse im Kundenstamm'}
            </span>
          </span>
        </div>
      </div>
    </div>
  )
  const dispatchCard = (
    <div>
      <h3 className={sectionTitle}>Versand</h3>
      <div className="rounded-2xl border border-border-default bg-bg-elevated/50 p-5">
        <h2 className="text-lg font-semibold">Rechnung per E-Mail senden</h2>
        <p className="mt-1 text-sm text-text-secondary">
          {previous
            ? `Zuletzt ${statusText(previous).toLowerCase()} am ${formatLogTime(previous.acceptedAt ?? previous.reservedAt)}. Erneutes Senden braucht eine eigene Bestätigung.`
            : 'PDF, Vorlage und Absender vor dem Versand prüfen.'}
        </p>
        <button
          id="invoice-mail-open"
          type="button"
          onClick={onOpenSheet}
          disabled={!usableSender}
          className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-accent text-base font-semibold text-bg-base hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Mail aria-hidden="true" className="h-5 w-5" />
          {previous ? 'Erneut senden …' : 'Per Mail senden'}
        </button>
        {!usableSender && (
          <p className="mt-2 text-center text-xs text-text-tertiary">
            Kein Absender mit Firma und Journal-Archiv verfügbar.
          </p>
        )}
      </div>
    </div>
  )

  return (
    <section aria-label="Rechnung per E-Mail" className="space-y-5">
      {latest && <ResultBanner entry={latest} />}

      {/* Mockup „Ergebnis“: after a dispatch the log and evidence come first. */}
      {entries.length === 0 && (
        <>
          {dataCard}
          {dispatchCard}
        </>
      )}

      {entries.length > 0 && (
        <DispatchLog entries={entries} onArchiveCheck={onArchiveCheck} checking={checking} />
      )}
      {archiveError && (
        <p role="alert" className="text-sm text-danger">
          {archiveError}
        </p>
      )}

      {latestWithId?.messageId && (
        <div>
          <h3 className={sectionTitle}>Nachweis</h3>
          <div className="rounded-2xl border border-border-default bg-bg-elevated/50 px-4 py-4">
            <strong className="block text-sm">Nachrichten-ID</strong>
            <p className="mt-1 break-all text-xs text-text-tertiary">{latestWithId.messageId}</p>
          </div>
        </div>
      )}

      {entries.length > 0 && (
        <a
          href={api.invoicePdfUrl(invoice.id)}
          target="_blank"
          rel="noreferrer"
          className="flex min-h-12 items-center justify-center gap-2 rounded-xl border border-border-default bg-bg-elevated text-base font-semibold hover:bg-bg-overlay"
        >
          <FileText aria-hidden="true" className="h-5 w-5" /> Rechnung ansehen
        </a>
      )}
      {entries.length > 0 && (
        <>
          {dispatchCard}
          {dataCard}
        </>
      )}
    </section>
  )
}
